import "@app/App.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Dashboard } from "@dto/Dashboard";
import { DashboardRightType, FullRightAccess, hasGlobalRightAccess } from "@dto/Users";
import { getWsButtonStyle, TW_ViewMenuItem, TW_ViewMenuItemIcon, useHover } from "@utils/twClasses";
import { Share2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore, useUserStore } from "src/store";
import DashboardSharingEditor from "./DashboardSharingEditor";

interface DashboardMenuShareItemProps {
  dashboard: Dashboard;
  onDashboardUpdate: (dashboard: Dashboard) => void;
  closeMenu: () => void;
}

function DashboardMenuShareItem({ dashboard, onDashboardUpdate, closeMenu }: DashboardMenuShareItemProps) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [updatedDashboard, setUpdatedDashboard] = useState<Dashboard>(dashboard);
  const wsHoverColor = useModelStore((state) => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle("transparent"), getWsButtonStyle(wsHoverColor));
  const user = useUserStore((state) => state.user);
  const userRole = useModelStore((state) => state.userRole);

  const hasFullDashboardAccess = dashboard.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, DashboardRightType, FullRightAccess, dashboard._key, true);

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasFullDashboardAccess) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
      onDashboardUpdate(updatedDashboard);
    }
  }

  const disabled = !hasFullDashboardAccess;

  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_ViewMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}>
        <Share2 className={TW_ViewMenuItemIcon} />
        {t("dashboard.button.share.text")}
      </DialogTrigger>
      <DialogContent className="w-full max-w-xl px-4">
        <DialogHeader>
          <DialogTitle>{t("dashboard.dialog.share.title") + " " + t("app.opening.quote") + dashboard.name + t("app.closing.quote")}</DialogTitle>
          <DialogDescription className="hidden">{t("dashboard.dialog.share.description")}</DialogDescription>
        </DialogHeader>
        <DashboardSharingEditor dashboard={dashboard} onClose={closeMenu} onDashboardUpdate={setUpdatedDashboard} />
      </DialogContent>
    </Dialog>
  );
}

export default DashboardMenuShareItem;
